import { Briefcase, ExternalLink, FileStack, Link2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import type { MunicipalityResourceLink } from "@/api/municipality_resource_links";
import { LaunchPadCaseLookup } from "@/components/launch_pad/LaunchPadCaseLookup";
import { LaunchPadInsightUpsellCard } from "@/components/launch_pad/LaunchPadInsightUpsellCard";
import type { ActiveSection, LaunchPadNavigateOptions } from "@/types/appNavigation";

type LaunchPadInsightsPanelProps = {
  municipalityId?: number;
  municipalityName?: string;
  showCaseLookup: boolean;
  showResourceLinks: boolean;
  showFileCenter: boolean;
  resourceLinks: MunicipalityResourceLink[];
  linksLoading: boolean;
  fileLibrarySummary: { documentCount: number; folderCount: number } | null;
  fileLibraryLoading: boolean;
  onNavigate: (section: ActiveSection, options?: LaunchPadNavigateOptions) => void;
};

export function LaunchPadInsightsPanel({
  municipalityId,
  municipalityName,
  showCaseLookup,
  showResourceLinks,
  showFileCenter,
  resourceLinks,
  linksLoading,
  fileLibrarySummary,
  fileLibraryLoading,
  onNavigate,
}: LaunchPadInsightsPanelProps) {
  return (
    <div className="space-y-4" data-testid="launch-pad-insights">
      {showCaseLookup ? (
        <LaunchPadCaseLookup onNavigate={onNavigate} />
      ) : (
        <LaunchPadInsightUpsellCard
          testId="launch-pad-insight-upsell-case-portal"
          title="Case Portal"
          description="Look up permitting cases and history."
          icon={Briefcase}
        />
      )}

      {showFileCenter ? (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-sm font-semibold">
              <FileStack className="h-4 w-4 text-primary" aria-hidden />
              File Center
            </CardTitle>
            <CardDescription>Municipal documents available to your team.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {fileLibraryLoading ? (
              <Skeleton className="h-8 w-full" />
            ) : fileLibrarySummary ? (
              <p className="text-sm text-muted-foreground">
                <span className="font-medium text-foreground">{fileLibrarySummary.documentCount}</span> documents
                in{" "}
                <span className="font-medium text-foreground">{fileLibrarySummary.folderCount}</span> folders
              </p>
            ) : (
              <p className="text-sm text-muted-foreground">No file library data yet.</p>
            )}
            <Button type="button" variant="outline" size="sm" onClick={() => onNavigate("file_viewer")}>
              Browse files
            </Button>
          </CardContent>
        </Card>
      ) : (
        <LaunchPadInsightUpsellCard
          testId="launch-pad-insight-upsell-file-center"
          title="File Center"
          description="Browse municipal PDFs and document folders."
          icon={FileStack}
        />
      )}

      {showResourceLinks ? (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-sm font-semibold">
              <Link2 className="h-4 w-4 text-primary" aria-hidden />
              Resource links
            </CardTitle>
            <CardDescription>Curated shortcuts for {municipalityName ?? "your municipality"}.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            {linksLoading ? (
              <div className="space-y-2">
                <Skeleton className="h-8 w-full" />
                <Skeleton className="h-8 w-full" />
              </div>
            ) : resourceLinks.length === 0 ? (
              <p className="text-sm text-muted-foreground">No active links configured.</p>
            ) : (
              <ul className="space-y-1">
                {resourceLinks.map((link) => (
                  <li key={link.id}>
                    <Button variant="ghost" className="h-auto w-full px-2 py-2 font-normal" asChild>
                      <a
                        href={link.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex w-full items-center gap-2"
                      >
                        <span className="min-w-0 flex-1 truncate text-left">{link.title}</span>
                        <ExternalLink className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden />
                      </a>
                    </Button>
                  </li>
                ))}
              </ul>
            )}
            <Button
              type="button"
              variant="link"
              className="h-auto px-2 text-xs"
              onClick={() => onNavigate("municipality_resource_links")}
            >
              Manage all links
            </Button>
          </CardContent>
        </Card>
      ) : (
        <LaunchPadInsightUpsellCard
          testId="launch-pad-insight-upsell-resource-links"
          title="Resource links"
          description={`Curated shortcuts for ${municipalityName ?? "your municipality"}.`}
          icon={Link2}
        />
      )}
    </div>
  );
}
